function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function messageFromProblemDetails(data: unknown): string | null {
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return messageFromProblemDetails(JSON.parse(trimmed) as unknown);
      } catch {
        return trimmed;
      }
    }
    return trimmed.length > 0 ? trimmed : null;
  }

  if (!isRecord(data)) {
    return null;
  }

  const detail = data.detail ?? data.Detail ?? data.title ?? data.Title;
  return typeof detail === 'string' && detail.trim() !== '' ? detail.trim() : null;
}

async function coerceToText(data: unknown): Promise<string | null> {
  if (typeof data === 'string') {
    return data;
  }

  if (!isRecord(data)) {
    return null;
  }

  if (typeof data.arrayBuffer === 'function') {
    const buffer = (await data.arrayBuffer()) as ArrayBuffer;
    return new TextDecoder().decode(buffer);
  }

  if (typeof data.text === 'function') {
    return String(await data.text());
  }

  return null;
}

function axiosResponseData(error: unknown): unknown {
  if (!isRecord(error)) {
    return undefined;
  }
  const response = error.response;
  if (!isRecord(response)) {
    return undefined;
  }
  return response.data;
}

/** Turns UM blob-download failures (JSON ProblemDetails wrapped as Blob) into a snackbar message. */
export async function readUmBlobErrorMessage(error: unknown, fallback: string): Promise<string> {
  const data = axiosResponseData(error);
  const text = await coerceToText(data);
  const fromBody = messageFromProblemDetails(text ?? data);
  if (fromBody) {
    return fromBody;
  }

  if (error instanceof Error && error.message.trim() !== '') {
    return error.message;
  }

  if (isRecord(error) && typeof error.message === 'string' && error.message.trim() !== '') {
    return error.message;
  }

  return fallback;
}

/** Rejects JSON error bodies that arrived with responseType blob (CSV or XLSX exports). */
export async function assertSpreadsheetBlob(blob: Blob, _fallbackFilename: string): Promise<void> {
  const type = blob.type.toLowerCase();
  if (type.includes('spreadsheet') || type.includes('octet-stream') || type.includes('csv') || type.includes('text/plain')) {
    return;
  }

  const text = await coerceToText(blob);
  const message = messageFromProblemDetails(text);
  if (message) {
    throw new Error(message);
  }
}
